import React, { useState, useEffect, useCallback } from "react";
import { X } from "lucide-react";
import { MEDIA } from "../lib/constants";

// localStorage key so we don't nag returning users on every page load.
const DISMISS_KEY = "dwc:free-joint-promo-dismissed";
const OPEN_DELAY_MS = 3000;

export default function PromoPopup() {
  const [open, setOpen] = useState(false);

  const close = useCallback(() => {
    setOpen(false);
    try {
      window.localStorage.setItem(DISMISS_KEY, "1");
    } catch (_) {
      /* ignore quota errors */
    }
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;
    // Respect the age gate — only fire after the gate has cleared
    const dismissed = window.localStorage.getItem(DISMISS_KEY) === "1";
    if (dismissed) return;

    const t = setTimeout(() => setOpen(true), OPEN_DELAY_MS);
    return () => clearTimeout(t);
  }, []);

  // Escape key closes the popup
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") close();
    };
    window.addEventListener("keydown", onKey);
    // Lock body scroll while open
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = originalOverflow;
    };
  }, [open, close]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4 animate-fade-in"
      role="dialog"
      aria-modal="true"
      aria-labelledby="promo-title"
      data-testid="promo-popup"
    >
      {/* Backdrop */}
      <button
        type="button"
        aria-label="Close promo"
        onClick={close}
        className="absolute inset-0 bg-ink/80 backdrop-blur-sm"
        data-testid="promo-popup-backdrop"
      />

      {/* Card */}
      <div className="relative max-w-[520px] w-full max-h-[92vh] overflow-hidden bg-white shadow-2xl">
        <button
          type="button"
          onClick={close}
          aria-label="Close"
          className="absolute top-3 right-3 z-10 w-9 h-9 flex items-center justify-center bg-white/90 hover:bg-brand hover:text-white text-ink border border-ink/10 transition-colors"
          data-testid="promo-popup-close"
        >
          <X size={18} />
        </button>

        <a
          href="/menu"
          onClick={close}
          className="block group"
          data-testid="promo-popup-link"
          aria-labelledby="promo-title"
        >
          <img
            src={MEDIA.freeJointPromo}
            alt="First joint's on us — new customers get a free pre-roll on their first visit at District Wellness Center."
            className="w-full h-auto block group-hover:scale-[1.01] transition-transform duration-500"
          />
          <span id="promo-title" className="sr-only">
            First joint&rsquo;s on us — free pre-roll for new customers
          </span>
        </a>

        <div className="p-4 bg-ink text-white flex items-center justify-between gap-3">
          <div className="text-[10px] uppercase tracking-[0.25em] text-white/70">
            New customers only · 21+ · While supplies last
          </div>
          <a
            href="/menu"
            onClick={close}
            className="text-[11px] uppercase tracking-[0.25em] text-brand hover:text-white transition-colors shrink-0"
            data-testid="promo-popup-cta"
          >
            Shop menu →
          </a>
        </div>
      </div>
    </div>
  );
}
